import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Container, Row } from 'react-bootstrap'
import { useIdleTimer } from 'react-idle-timer'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useMatches, useNavigate } from 'react-router'
import { currentUserPersonQuery } from '../api'
import { AlertLogout } from '../common/alertlogout'
import { topPath } from '../common/settings.js'
import { MainContainer, PageContentContainer } from '../layout/AuthenticatedRoutes.styles.tsx'
import TopMenu from '../layout/TopMenu.tsx'
import { hrEsalary } from '../main'
import { authSelector } from '../reducers'

const IDLE_TIME_MIN = 30 // logout after this idle time
const pathRE = /\/*([^/]+)(?:\/+(.+))?/

/**
 * AuthenticatedLayout - Layout for authenticated users
 * Handles:
 * - Idle timeout and auto-logout
 * - Current user person data fetching
 * - Page title updates
 * - Permission-based route access
 * - Sidebar and content layout
 */
export const AuthenticatedLayout = () => {
	const dispatch = useDispatch()
	const auth = useSelector(authSelector)
	const navigate = useNavigate()
	const location = useLocation()
	const matches = useMatches()

	// Get route metadata from the current route
	const currentMatch = matches[matches.length - 1]
	const routeMetadata = currentMatch?.handle || {}

	const currentUserPerson = useQuery(currentUserPersonQuery(auth?.employeeNumber)).data

	// Handle personal profile redirect
	useEffect(() => {
		if (auth?.personalProfile && !location.pathname.startsWith('/personal')) {
			navigate('/personal/dashboard')
		}
	}, [auth, location.pathname, navigate])

	// Update page title and topPath on route change
	useEffect(() => {
		const pathName = location.pathname.slice(1) || 'dashboard'
		document.title = `HR${window.location.hostname === hrEsalary ? '' : '²'} easy ${pathName}`

		// remember top menu path for each section, so that user is returned to the same subpage when clicking the top menu again
		const pathParts = pathName.match(pathRE)
		if (pathParts[2] !== undefined) {
			topPath[pathParts[1]] = pathParts[2]
		}
	}, [location.pathname])

	// Permission-based access control
	useEffect(() => {
		const isBasicLevel = auth?.hrLevel !== 'premium'
		const { premium, equipment, personal } = routeMetadata

		// Basic users cannot access premium routes
		if (isBasicLevel && premium) {
			navigate('/premium')
			return
		}

		// Personal profile users can only access personal routes
		if (auth?.personalProfile && !personal && location.pathname !== '/') {
			navigate('/personal/dashboard')
			return
		}

		// Equipment managers can only access equipment routes
		if (auth?.role === 'EQUIPMENT') {
			if (!equipment && !personal) {
				navigate('/')
			}
		}
	}, [auth, routeMetadata, navigate, location.pathname])

	// Idle timeout logout
	const logout = useCallback(() => {
		dispatch({ type: 'LOGOUT_SUCCEEDED' })
	}, [dispatch])

	const [showAlert, setShowAlert] = useState(false)

	const { activate } = useIdleTimer({
		timeout: (IDLE_TIME_MIN - 1) * 60 * 1000,
		promptBeforeIdle: 60 * 1000,
		onPrompt: () => setShowAlert(true),
		onIdle: logout,
	})

	const resume = () => {
		activate()
		setShowAlert(false)
	}

	// Render sidebar from route metadata
	const renderSidebar = () => {
		const { sidebar } = routeMetadata
		if (sidebar === null) {
			return null
		}
		if (typeof sidebar === 'function') {
			const SidebarComponent = sidebar
			return <SidebarComponent />
		}
		return null
	}

	// Determine background class
	const getBgClass = () => {
		if (routeMetadata.noBgImage) {
			return ''
		}
		return 'bg-image-illustration2'
	}

	return (
		<>
			{showAlert && <AlertLogout click={resume} />}
			<Container fluid className="vh-100">
				<Row className="wrap flex-nowrap">
					{renderSidebar()}
					<MainContainer className={`col bg-transparent ${getBgClass()}`}>
						<TopMenu currentUserPerson={currentUserPerson} />
						<PageContentContainer>
							<Outlet context={{ currentUserPerson }} />
						</PageContentContainer>
					</MainContainer>
				</Row>
			</Container>
		</>
	)
}
